import { ApiResponse } from '../types';
import {
  FulfilmentOptionsRequest,
  FulfilmentOptionsResponse,
  InternalFulfilmentV3OptionsResponse,
  ScheduledWindowsRetrieveRequest,
  ScheduledWindowsRetrieveResponse,
} from './types';
import { CountryCode, getMultiTenancyHeaders } from '../utils/multiTenancyHeaders';
import { generateRecaptchaHeaders } from '../utils/recaptcha/generateRecaptchaHeaders';
import { ApiClient } from '../client/ApiClient';
import { getShopifyAuthHeader, ShopifyTokenGenerator } from '../utils/shopifySessionTokens';

export const FULFILMENT_API_RECAPTCHA_ACTION = 'fulfilmentApi';

const hasApiError = (fulfilmentResponse: FulfilmentOptionsResponse | null) => {
  return !!fulfilmentResponse?.delivery?.presentation?.find(pres => pres?.type === 'Error');
};

export class FulfilmentClient extends ApiClient {
  private readonly _fulfillmentBaseUrl: string;
  private readonly _fulfillmentOptionsUrl: string;
  private readonly _fulfillmentMultipleOptionsUrl: string;
  private readonly _fulfillmentRetrieveScheduledWindowsUrl: string;
  private readonly _apiKey?: string;
  private readonly _countryCode: CountryCode;
  private readonly _shopifyTokenGenerator?: ShopifyTokenGenerator;

  constructor({
    fulfillmentBaseUrl,
    fulfillmentOptionsUrl = '/FulfilmentOptions',
    fulfillmentMultipleOptionsUrl = '/FulfilmentOptionsCombined',
    fulfillmentRetrieveScheduledWindowsUrl = '/ScheduledWindows/Retrieve',
    apiKey,
    countryCode = 'AU',
    shopifyTokenGenerator,
  }: {
    fulfillmentBaseUrl: string;
    fulfillmentOptionsUrl?: string;
    fulfillmentMultipleOptionsUrl?: string;
    fulfillmentOptionsValidateUrl?: string;
    fulfillmentRetrieveScheduledWindowsUrl?: string;
    apiKey?: string;
    countryCode?: CountryCode;
    shopifyTokenGenerator?: ShopifyTokenGenerator;
  }) {
    super();
    this._fulfillmentBaseUrl = fulfillmentBaseUrl;
    this._fulfillmentOptionsUrl = fulfillmentOptionsUrl;
    this._fulfillmentMultipleOptionsUrl = fulfillmentMultipleOptionsUrl;
    this._fulfillmentRetrieveScheduledWindowsUrl = fulfillmentRetrieveScheduledWindowsUrl;
    this._apiKey = apiKey;
    this._countryCode = countryCode;
    this._shopifyTokenGenerator = shopifyTokenGenerator;
  }

  override get requestHeaders(): Record<string, string> {
    return {
      ...super.requestHeaders,
      ...getMultiTenancyHeaders(this._countryCode),
      ...(this._apiKey ? { 'Ocp-Apim-Subscription-Key': this._apiKey } : {}),
    };
  }

  generateCheckoutHeaders = async () => {
    return {
      headers: await getShopifyAuthHeader(this._shopifyTokenGenerator),
    };
  };

  getOptions = async (
    body: FulfilmentOptionsRequest,
    recaptchaToken: string
  ): Promise<ApiResponse<FulfilmentOptionsResponse>> => {
    const { data, statusCode, error } = await this._fetch<InternalFulfilmentV3OptionsResponse>({
      path: `${this._fulfillmentBaseUrl}${this._fulfillmentOptionsUrl}`,
      method: 'POST',
      body,
      headers: { ...generateRecaptchaHeaders(recaptchaToken, FULFILMENT_API_RECAPTCHA_ACTION) },
    });

    if (error) {
      return { error, statusCode };
    }

    /* Checking for errors only in the delivery payload to see if the whole product has gone wrong
    /* but to avoid causing an issue where ClickAndCollect has an error but Delivery doesn't
    /* TODO: Move this error-handling down the chain to make it independent of fulfilment type */
    const wasApiError = Boolean(data?.delivery?.presentation?.find(pres => pres?.type === 'Error'));

    if (wasApiError) {
      return { error: { message: 'Internal API Error' }, statusCode: 500 };
    }

    const processedData: FulfilmentOptionsResponse = {
      clickAndCollect: data?.clickAndCollect,
      cashAndCarry: data?.cashAndCarry,
      delivery: data?.delivery as FulfilmentOptionsResponse['delivery'],
    };

    return { data: processedData, statusCode };
  };

  getMultipleOptions = async (
    body: Array<FulfilmentOptionsRequest | null>,
    recaptchaToken: string
  ): Promise<ApiResponse<Array<InternalFulfilmentV3OptionsResponse | null>>> => {
    const { data, statusCode, error } = await this._fetch<Array<FulfilmentOptionsResponse>>({
      path: `${this._fulfillmentBaseUrl}${this._fulfillmentMultipleOptionsUrl}`,
      method: 'POST',
      body,
      headers: { ...generateRecaptchaHeaders(recaptchaToken, FULFILMENT_API_RECAPTCHA_ACTION) },
    });

    if (error) {
      return { error, statusCode };
    }

    /* Checking for errors only in the delivery payload to see if the whole product has gone wrong
    /* but to avoid causing an issue where ClickAndCollect has an error but Delivery doesn't
    /* TODO: Move this error-handling down the chain to make it independent of fulfilment type */
    const errorCheckedData = data?.map(d => (hasApiError(d) ? null : d));
    if (errorCheckedData?.every(d => d === null)) {
      return { error: { message: 'Internal API Error' }, statusCode: 500 };
    }

    return { data: errorCheckedData as Array<InternalFulfilmentV3OptionsResponse | null>, statusCode };
  };

  retrieveScheduledWindows = async (
    body: ScheduledWindowsRetrieveRequest
  ): Promise<ApiResponse<ScheduledWindowsRetrieveResponse>> => {
    const { headers: requestHeaders } = await this.generateCheckoutHeaders();
    if (!requestHeaders) {
      return { statusCode: 500, error: new Error('Failed to generate headers') };
    }

    const { data, statusCode, error } = await this._fetch<ScheduledWindowsRetrieveResponse>({
      path: `${this._fulfillmentBaseUrl}${this._fulfillmentRetrieveScheduledWindowsUrl}`,
      method: 'POST',
      body,
      headers: requestHeaders,
    });

    if (error) {
      return { error, statusCode };
    }

    if (!data) {
      return { statusCode, error: new Error('Fulfillment API did not return data') };
    }

    return { data, statusCode };
  };
}
